import { SURFACE, add, cross, normalize, scale, sub } from './builder.js';
import type { CourseBuilder, Frame, Material, MapStyle, RailKind, V3 } from './builder.js';

const UP: V3 = [0, 1, 0];

export interface Rect {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

function footprint(b: CourseBuilder, f: Frame, style: MapStyle, pts: [number, number][]): void {
  b.shape(
    style,
    pts.map(([lx, lz]) => {
      const p = f.p(lx, 0, lz);
      return [p[0], p[2]];
    }),
  );
}

/** Render-only tube along a polyline (rails, coping, pipes). */
export function tube(
  b: CourseBuilder,
  points: V3[],
  radius: number,
  mat: Material,
  options: { loop?: boolean; sides?: number } = {},
): void {
  const sides = options.sides ?? 8;
  const loop = options.loop ?? false;
  const count = points.length;
  if (count < 2) return;
  const rings: { p: V3; n: V3 }[][] = [];
  for (let i = 0; i < count; i += 1) {
    const prev = points[loop ? (i - 1 + count) % count : Math.max(0, i - 1)]!;
    const next = points[loop ? (i + 1) % count : Math.min(count - 1, i + 1)]!;
    const tangent = normalize(sub(next, prev));
    let side = cross(tangent, UP);
    if (Math.hypot(side[0], side[1], side[2]) < 1e-3) side = [1, 0, 0];
    side = normalize(side);
    const normal = normalize(cross(side, tangent));
    const ring: { p: V3; n: V3 }[] = [];
    for (let s = 0; s <= sides; s += 1) {
      const angle = (s / sides) * Math.PI * 2;
      const n = add(scale(normal, Math.cos(angle)), scale(side, Math.sin(angle)));
      ring.push({ p: add(points[i]!, scale(n, radius)), n });
    }
    rings.push(ring);
  }
  if (loop) rings.push(rings[0]!);
  for (let i = 0; i < rings.length - 1; i += 1) {
    for (let s = 0; s < sides; s += 1) {
      const a = rings[i]![s]!;
      const c = rings[i + 1]![s]!;
      const d = rings[i + 1]![s + 1]!;
      const e = rings[i]![s + 1]!;
      const options2 = { mat, collide: false };
      b.tri(a.p, c.p, d.p, options2, { normals: [a.n, c.n, d.n] });
      b.tri(a.p, d.p, e.p, options2, { normals: [a.n, d.n, e.n] });
    }
  }
}

/** Render-only vertical post. */
export function post(
  b: CourseBuilder,
  base: V3,
  height: number,
  radius: number,
  mat: Material = 'metal',
): void {
  tube(b, [base, add(base, [0, height, 0])], radius, mat, { sides: 6 });
}

export interface BoxOptions {
  top: Material;
  side: Material;
  rails?: 'long' | 'all' | 'none';
  railKind?: RailKind;
  map?: MapStyle | null;
  bottom?: boolean;
  collide?: boolean;
  render?: boolean;
  flags?: number;
  /** Local faces to omit (hidden faces inside composite features). */
  skip?: ('px' | 'nx' | 'pz' | 'nz')[];
}

/** Axis-aligned (in frame space) box with its base at local (x, y, z). */
export function box(
  b: CourseBuilder,
  f: Frame,
  x: number,
  y: number,
  z: number,
  w: number,
  h: number,
  d: number,
  options: BoxOptions,
): void {
  const hw = w / 2;
  const hd = d / 2;
  const c = (sx: number, sy: number, sz: number): V3 => f.p(x + sx * hw, y + sy * h, z + sz * hd);
  const surface = {
    collide: options.collide ?? true,
    render: options.render ?? true,
    flags: options.flags ?? 0,
  };
  const quad = (a: V3, bb: V3, cc: V3, dd: V3, facing: V3, mat: Material, shade: number[]) => {
    const n = normalize(facing);
    const extras = { normals: [n, n, n] as [V3, V3, V3] };
    b.tri(a, bb, cc, { mat, ...surface, shade: [shade[0]!, shade[1]!, shade[2]!] }, extras);
    b.tri(a, cc, dd, { mat, ...surface, shade: [shade[0]!, shade[2]!, shade[3]!] }, extras);
  };
  const top = [1, 1, 1, 1];
  const sideShade = [0.72, 0.72, 1, 1];
  const skip = new Set(options.skip ?? []);
  quad(c(-1, 1, -1), c(1, 1, -1), c(1, 1, 1), c(-1, 1, 1), UP, options.top, top);
  if (!skip.has('pz')) {
    quad(c(-1, 0, 1), c(1, 0, 1), c(1, 1, 1), c(-1, 1, 1), f.d(0, 0, 1), options.side, sideShade);
  }
  if (!skip.has('nz')) {
    quad(
      c(1, 0, -1),
      c(-1, 0, -1),
      c(-1, 1, -1),
      c(1, 1, -1),
      f.d(0, 0, -1),
      options.side,
      sideShade,
    );
  }
  if (!skip.has('px')) {
    quad(c(1, 0, 1), c(1, 0, -1), c(1, 1, -1), c(1, 1, 1), f.d(1, 0, 0), options.side, sideShade);
  }
  if (!skip.has('nx')) {
    quad(
      c(-1, 0, -1),
      c(-1, 0, 1),
      c(-1, 1, 1),
      c(-1, 1, -1),
      f.d(-1, 0, 0),
      options.side,
      sideShade,
    );
  }
  if (options.bottom) {
    quad(c(-1, 0, 1), c(1, 0, 1), c(1, 0, -1), c(-1, 0, -1), [0, -1, 0], options.side, top);
  }
  const rails = options.rails ?? 'none';
  const kind = options.railKind ?? 'ledge';
  const inset = 0.04;
  const yTop = y + h;
  if (rails === 'long' || rails === 'all') {
    const alongX = w >= d;
    if (alongX || rails === 'all') {
      for (const sz of [-1, 1]) {
        b.rail(kind, [
          f.p(x - hw + 0.05, yTop, z + sz * (hd - inset)),
          f.p(x + hw - 0.05, yTop, z + sz * (hd - inset)),
        ]);
      }
    }
    if (!alongX || rails === 'all') {
      for (const sx of [-1, 1]) {
        b.rail(kind, [
          f.p(x + sx * (hw - inset), yTop, z - hd + 0.05),
          f.p(x + sx * (hw - inset), yTop, z + hd - 0.05),
        ]);
      }
    }
  }
  if (options.map !== null) {
    footprint(b, f, options.map ?? 'block', [
      [x - hw, z - hd],
      [x + hw, z - hd],
      [x + hw, z + hd],
      [x - hw, z + hd],
    ]);
  }
}

export interface QuarterPipeOptions {
  width: number;
  radius: number;
  /** Final transition angle in degrees; 90 reaches vertical. */
  angle?: number;
  /** Vertical extension above a 90° transition. */
  vert?: number;
  deck?: number;
  back?: boolean;
  sides?: boolean;
  coping?: boolean;
  mat?: Material;
  sideMat?: Material;
  deckMat?: Material;
  flags?: number;
  segments?: number;
  map?: MapStyle | null;
}

export interface QuarterPipeInfo {
  height: number;
  /** Local z of the lip (negative). */
  lipZ: number;
  /** Local z of the back of the deck (negative). */
  backZ: number;
}

/**
 * A curved transition that rises toward local -z. A rider approaching from +z rides up it.
 * Local x spans the width, centred on the frame origin.
 */
export function quarterPipe(
  b: CourseBuilder,
  f: Frame,
  options: QuarterPipeOptions,
): QuarterPipeInfo {
  const width = options.width;
  const R = options.radius;
  const theta = ((options.angle ?? 90) * Math.PI) / 180;
  const vert = theta >= Math.PI / 2 - 1e-6 ? (options.vert ?? 0) : 0;
  const deck = options.deck ?? 2;
  const segments = options.segments ?? Math.max(8, Math.round((theta / (Math.PI / 2)) * 16));
  const mat = options.mat ?? 'ramp';
  const sideMat = options.sideMat ?? 'rampSide';
  const deckMat = options.deckMat ?? 'deck';
  const flags = (options.flags ?? 0) | SURFACE.transition;
  const arcTop = R * (1 - Math.cos(theta));
  const height = arcTop + vert;
  const lipZ = -R * Math.sin(theta);
  const backZ = lipZ - deck;
  const hw = width / 2;
  const across = Math.max(1, Math.ceil(width / 8));

  b.grid(
    across,
    segments,
    (i, j) => {
      const t = (j / segments) * theta;
      const lx = -hw + (i / across) * width;
      return {
        p: f.p(lx, R * (1 - Math.cos(t)), -R * Math.sin(t)),
        n: f.d(0, Math.cos(t), Math.sin(t)),
        uv: [lx, R * t],
      };
    },
    { mat, flags },
    (_i, j) => 0.8 + 0.2 * (j / segments),
  );
  if (vert > 0) {
    b.grid(
      across,
      1,
      (i, j) => {
        const lx = -hw + (i / across) * width;
        return {
          p: f.p(lx, arcTop + j * vert, lipZ),
          n: f.d(0, 0, 1),
          uv: [lx, R * theta + j * vert],
        };
      },
      { mat, flags },
    );
  }
  if (deck > 0) {
    b.quad(
      f.p(-hw, height, lipZ),
      f.p(hw, height, lipZ),
      f.p(hw, height, backZ),
      f.p(-hw, height, backZ),
      UP,
      { mat: deckMat },
    );
  }
  if (options.back ?? true) {
    b.quad(
      f.p(hw, 0, backZ),
      f.p(-hw, 0, backZ),
      f.p(-hw, height, backZ),
      f.p(hw, height, backZ),
      f.d(0, 0, -1),
      { mat: sideMat, shade: [0.7, 0.7, 1] },
    );
  }
  if (options.sides ?? true) {
    for (const sx of [-1, 1]) {
      const lx = sx * hw;
      const pts: V3[] = [f.p(lx, 0, backZ), f.p(lx, height, backZ)];
      if (deck > 0) pts.push(f.p(lx, height, lipZ));
      if (vert > 0) pts.push(f.p(lx, arcTop, lipZ));
      for (let j = segments - (vert > 0 ? 1 : 0); j >= 0; j -= 1) {
        if (vert === 0 && deck === 0 && j === segments) {
          pts.push(f.p(lx, height, lipZ));
          continue;
        }
        const t = (j / segments) * theta;
        pts.push(f.p(lx, R * (1 - Math.cos(t)), -R * Math.sin(t)));
      }
      b.fan(pts, f.d(sx, 0, 0), { mat: sideMat });
    }
  }
  if (options.coping ?? true) {
    const a = f.p(-hw, height, lipZ);
    const c = f.p(hw, height, lipZ);
    b.rail('coping', [a, c]);
    tube(b, [add(a, f.d(0, -0.04, 0.02)), add(c, f.d(0, -0.04, 0.02))], 0.06, 'metal');
  }
  if (options.map !== null) {
    footprint(b, f, options.map ?? 'ramp', [
      [-hw, 0],
      [hw, 0],
      [hw, backZ],
      [-hw, backZ],
    ]);
  }
  return { height, lipZ, backZ };
}

export interface WedgeOptions {
  width: number;
  length: number;
  height: number;
  back?: boolean;
  mat?: Material;
  sideMat?: Material;
  flags?: number;
  map?: MapStyle | null;
}

/** A planar bank rising from local z=0 (ground) to z=-length. */
export function wedge(b: CourseBuilder, f: Frame, options: WedgeOptions): void {
  const { width, length, height } = options;
  const hw = width / 2;
  const mat = options.mat ?? 'ramp';
  const sideMat = options.sideMat ?? 'rampSide';
  const normal = normalize(f.d(0, length, height));
  b.quad(
    f.p(-hw, 0, 0),
    f.p(hw, 0, 0),
    f.p(hw, height, -length),
    f.p(-hw, height, -length),
    normal,
    { mat, flags: options.flags ?? 0, shade: [0.85, 0.85, 1] },
  );
  for (const sx of [-1, 1]) {
    b.fan(
      [f.p(sx * hw, 0, -length), f.p(sx * hw, height, -length), f.p(sx * hw, 0, 0)],
      f.d(sx, 0, 0),
      { mat: sideMat },
    );
  }
  if (options.back ?? true) {
    b.quad(
      f.p(hw, 0, -length),
      f.p(-hw, 0, -length),
      f.p(-hw, height, -length),
      f.p(hw, height, -length),
      f.d(0, 0, -1),
      { mat: sideMat },
    );
  }
  if (options.map !== null) {
    footprint(b, f, options.map ?? 'ramp', [
      [-hw, 0],
      [hw, 0],
      [hw, -length],
      [-hw, -length],
    ]);
  }
}

export interface StairsOptions {
  width: number;
  steps: number;
  rise: number;
  run: number;
  mat?: Material;
  handrails?: boolean;
  railInset?: number;
}

/**
 * Stairs descending toward local +z from a landing at z=0. Collision is the smooth slope
 * through the step nosings so riders roll down them.
 */
export function stairs(b: CourseBuilder, f: Frame, options: StairsOptions): void {
  const { width, steps, rise, run } = options;
  const mat = options.mat ?? 'concrete';
  const hw = width / 2;
  const H = steps * rise;
  const L = steps * run;
  for (let i = 0; i < steps; i += 1) {
    const top = H - i * rise;
    const tread = H - (i + 1) * rise;
    const z0 = i * run;
    const z1 = (i + 1) * run;
    b.quad(
      f.p(-hw, tread, z0),
      f.p(hw, tread, z0),
      f.p(hw, top, z0),
      f.p(-hw, top, z0),
      f.d(0, 0, 1),
      { mat, collide: false, shade: [0.75, 0.75, 1] },
    );
    if (i < steps - 1) {
      b.quad(f.p(-hw, tread, z0), f.p(-hw, tread, z1), f.p(hw, tread, z1), f.p(hw, tread, z0), UP, {
        mat,
        collide: false,
      });
    }
  }
  const slopeNormal = normalize(f.d(0, L, H));
  b.quad(f.p(-hw, H, 0), f.p(hw, H, 0), f.p(hw, 0, L), f.p(-hw, 0, L), slopeNormal, {
    mat,
    render: false,
  });
  for (const sx of [-1, 1]) {
    const pts: V3[] = [f.p(sx * hw, 0, 0), f.p(sx * hw, H, 0)];
    for (let i = 0; i < steps; i += 1) {
      pts.push(f.p(sx * hw, H - (i + 1) * rise, i * run));
      pts.push(f.p(sx * hw, H - (i + 1) * rise, (i + 1) * run));
    }
    b.fan(pts, f.d(sx, 0, 0), { mat, collide: false });
    b.fan([f.p(sx * hw, 0, 0), f.p(sx * hw, H, 0), f.p(sx * hw, 0, L)], f.d(sx, 0, 0), {
      mat,
      render: false,
    });
  }
  if (options.handrails ?? true) {
    const inset = options.railInset ?? 0.25;
    for (const sx of [-1, 1]) {
      const lx = sx * (hw - inset);
      const railH = 0.9;
      const pts: V3[] = [
        f.p(lx, H + railH, -1.2),
        f.p(lx, H + railH, 0),
        f.p(lx, railH, L),
        f.p(lx, railH, L + 1.2),
      ];
      railWithPosts(b, pts, sx < 0 ? 'railYellow' : 'railRed', [
        f.p(lx, H, -1.1),
        f.p(lx, H, 0.05),
        f.p(lx, 0, L - 0.05),
        f.p(lx, 0, L + 1.1),
      ]);
    }
  }
  footprint(b, f, 'block', [
    [-hw, 0],
    [hw, 0],
    [hw, L],
    [-hw, L],
  ]);
}

/**
 * A grind rail along `points` (rail top). Posts are dropped from `postBases` (or from each
 * point straight down to y=0).
 */
export function railWithPosts(
  b: CourseBuilder,
  points: V3[],
  mat: Material = 'railRed',
  postBases?: V3[],
  radius = 0.045,
  postStride = 1,
): void {
  b.rail('rail', points);
  tube(b, points, radius, mat, { sides: 8 });
  const bases = postBases ?? points.map((p) => [p[0], 0, p[2]] as V3);
  for (let i = 0; i < points.length; i += 1) {
    if (i % postStride !== 0 && i !== points.length - 1) continue;
    const top = points[i]!;
    const base = bases[i]!;
    const height = top[1] - base[1] - radius;
    if (height > 0.05) post(b, base, height, 0.035, 'metal');
  }
  b.shape(
    'rail',
    points.map((p) => [p[0], p[2]]),
    false,
  );
}

/** Straight rail between two points at a given height, posts every ~3 m. */
export function flatRail(
  b: CourseBuilder,
  from: V3,
  to: V3,
  mat: Material = 'railRed',
  segments?: number,
): void {
  const distance = Math.hypot(to[0] - from[0], to[2] - from[2]);
  const count = segments ?? Math.max(1, Math.round(distance / 3));
  const pts: V3[] = [];
  for (let i = 0; i <= count; i += 1) {
    const t = i / count;
    pts.push([
      from[0] + (to[0] - from[0]) * t,
      from[1] + (to[1] - from[1]) * t,
      from[2] + (to[2] - from[2]) * t,
    ]);
  }
  railWithPosts(b, pts, mat);
}

export interface HubbaOptions {
  width: number;
  length: number;
  topHigh: number;
  topLow: number;
  mat?: Material;
}

/** Sloped ledge from local z=0 (high end) to z=length (low end). */
export function hubba(b: CourseBuilder, f: Frame, x: number, options: HubbaOptions): void {
  const { width, length, topHigh, topLow } = options;
  const mat = options.mat ?? 'granite';
  const hw = width / 2;
  const top = normalize(f.d(0, length, topHigh - topLow));
  b.quad(
    f.p(x - hw, topHigh, 0),
    f.p(x + hw, topHigh, 0),
    f.p(x + hw, topLow, length),
    f.p(x - hw, topLow, length),
    top,
    { mat },
  );
  b.quad(
    f.p(x - hw, 0, length),
    f.p(x + hw, 0, length),
    f.p(x + hw, topLow, length),
    f.p(x - hw, topLow, length),
    f.d(0, 0, 1),
    { mat },
  );
  for (const sx of [-1, 1]) {
    b.fan(
      [
        f.p(x + sx * hw, 0, 0),
        f.p(x + sx * hw, topHigh, 0),
        f.p(x + sx * hw, topLow, length),
        f.p(x + sx * hw, 0, length),
      ],
      f.d(sx, 0, 0),
      { mat, shade: [0.75, 1, 1] },
    );
    b.rail('ledge', [
      f.p(x + sx * (hw - 0.04), topHigh, 0.05),
      f.p(x + sx * (hw - 0.04), topLow, length - 0.05),
    ]);
  }
  footprint(b, f, 'block', [
    [x - hw, 0],
    [x + hw, 0],
    [x + hw, length],
    [x - hw, length],
  ]);
}

export interface BowlOptions {
  cx: number;
  cz: number;
  /** Half extents of the straight core of the floor outline (0 for circles). */
  hx: number;
  hz: number;
  /** Floor corner radius. */
  corner: number;
  /** Transition radius. */
  radius: number;
  depth: number;
  plot: Rect;
  tileBand?: number;
  floorMat?: Material;
  wallMat?: Material;
  deckMat?: Material;
  flags?: number;
  arcSegments?: number;
}

interface OutlineSample {
  base: [number, number];
  normal: [number, number];
}

function outlineSamples(options: BowlOptions): OutlineSample[] {
  const { hx, hz, plot, cx, cz } = options;
  const samples: OutlineSample[] = [];
  const corners: [number, number, number][] = [
    [hx, hz, 0],
    [-hx, hz, Math.PI / 2],
    [-hx, -hz, Math.PI],
    [hx, -hz, (Math.PI * 3) / 2],
  ];
  const plotCorners: [number, number][] = [
    [plot.x1 - cx, plot.z1 - cz],
    [plot.x0 - cx, plot.z1 - cz],
    [plot.x0 - cx, plot.z0 - cz],
    [plot.x1 - cx, plot.z0 - cz],
  ];
  const edgeStep = 3;
  const arcStep = Math.PI / 36;
  for (let c = 0; c < 4; c += 1) {
    const [bx, bz, start] = corners[c]!;
    // Straight edge leading into this corner (edge at angle `start`).
    const [px, pz] = corners[(c + 3) % 4]!;
    const edgeLength = Math.hypot(bx - px, bz - pz);
    const count = edgeLength > 1e-6 ? Math.max(1, Math.ceil(edgeLength / edgeStep)) : 0;
    for (let i = 0; i < count; i += 1) {
      const t = i / count;
      samples.push({
        base: [px + (bx - px) * t, pz + (bz - pz) * t],
        normal: [Math.cos(start), Math.sin(start)],
      });
    }
    const angles: number[] = [];
    for (let a = start; a < start + Math.PI / 2 - 1e-6; a += arcStep) angles.push(a);
    const [pcx, pcz] = plotCorners[c]!;
    const cornerAngle = Math.atan2(pcz - bz, pcx - bx);
    let normalizedCorner = cornerAngle;
    while (normalizedCorner < start) normalizedCorner += Math.PI * 2;
    if (normalizedCorner < start + Math.PI / 2) angles.push(normalizedCorner);
    angles.sort((m, n) => m - n);
    for (const a of angles) samples.push({ base: [bx, bz], normal: [Math.cos(a), Math.sin(a)] });
  }
  return samples;
}

/** Sunken pool/bowl/channel carved below y=0 with its own deck filling `plot`. */
export function sunkenBowl(b: CourseBuilder, options: BowlOptions): void {
  const { cx, cz, corner: rc, radius: R, depth: D, plot } = options;
  const floorMat = options.floorMat ?? 'plaster';
  const wallMat = options.wallMat ?? 'plaster';
  const deckMat = options.deckMat ?? 'concrete';
  const tileBand = options.tileBand ?? 0;
  const flags = (options.flags ?? 0) | 1;
  const theta = D >= R ? Math.PI / 2 : Math.acos((R - D) / R);
  const arcTop = R * (1 - Math.cos(theta));
  const vert = Math.max(0, D - R);
  const topOffset = R * Math.sin(theta);
  const samples = outlineSamples(options);
  samples.push(samples[0]!);
  const at = (s: OutlineSample, offset: number, y: number): V3 => [
    cx + s.base[0] + s.normal[0] * offset,
    y,
    cz + s.base[1] + s.normal[1] * offset,
  ];
  const perimeter: number[] = [0];
  for (let i = 1; i < samples.length; i += 1) {
    const a = at(samples[i - 1]!, rc + topOffset * 0.5, 0);
    const c = at(samples[i]!, rc + topOffset * 0.5, 0);
    perimeter.push(perimeter[i - 1]! + Math.hypot(c[0] - a[0], c[2] - a[2]));
  }
  const nu = samples.length - 1;

  // Floor: straight core plus the rounded ring out to the transition start.
  if (options.hx > 0 && options.hz > 0) {
    b.quad(
      [cx - options.hx, -D, cz - options.hz],
      [cx + options.hx, -D, cz - options.hz],
      [cx + options.hx, -D, cz + options.hz],
      [cx - options.hx, -D, cz + options.hz],
      UP,
      { mat: floorMat, flags, shade: [0.78, 0.78, 0.78] },
    );
  }
  for (let i = 0; i < nu; i += 1) {
    const s0 = samples[i]!;
    const s1 = samples[i + 1]!;
    const n: [V3, V3, V3] = [UP, UP, UP];
    const a = at(s0, 0, -D);
    const c = at(s1, 0, -D);
    const d = at(s1, rc, -D);
    const e = at(s0, rc, -D);
    b.tri(a, c, d, { mat: floorMat, flags, shade: [0.78, 0.78, 0.82] }, { normals: n });
    b.tri(a, d, e, { mat: floorMat, flags, shade: [0.78, 0.82, 0.82] }, { normals: n });
  }

  // Walls: arc then optional vertical extension, with an optional tile band on top.
  const arcSegments = options.arcSegments ?? 12;
  const rows: { offset: number; y: number; n: (s: OutlineSample) => V3; v: number }[] = [];
  for (let j = 0; j <= arcSegments; j += 1) {
    const t = (j / arcSegments) * theta;
    rows.push({
      offset: rc + R * Math.sin(t),
      y: -D + R * (1 - Math.cos(t)),
      n: (s) => [-s.normal[0] * Math.sin(t), Math.cos(t), -s.normal[1] * Math.sin(t)],
      v: R * t,
    });
  }
  if (vert > 0) {
    const plasterTop = Math.max(0, vert - tileBand);
    const top = rows[rows.length - 1]!;
    if (plasterTop > 0.01) {
      rows.push({
        offset: rc + R,
        y: -D + arcTop + plasterTop,
        n: (s) => [-s.normal[0], 0, -s.normal[1]],
        v: top.v + plasterTop,
      });
    }
  }
  const tileRows: typeof rows = [];
  if (vert > 0 && tileBand > 0) {
    const bandStart = Math.max(0, vert - tileBand);
    const baseV = R * theta;
    tileRows.push({
      offset: rc + R,
      y: -D + arcTop + bandStart,
      n: (s) => [-s.normal[0], 0, -s.normal[1]],
      v: baseV + bandStart,
    });
    tileRows.push({
      offset: rc + R,
      y: 0,
      n: (s) => [-s.normal[0], 0, -s.normal[1]],
      v: baseV + vert,
    });
  } else if (vert > 0) {
    rows.push({
      offset: rc + R,
      y: 0,
      n: (s) => [-s.normal[0], 0, -s.normal[1]],
      v: R * theta + vert,
    });
  }
  const wall = (list: typeof rows, mat: Material, shadeRows: boolean) =>
    b.grid(
      nu,
      list.length - 1,
      (i, j) => {
        const s = samples[i]!;
        const row = list[j]!;
        return { p: at(s, row.offset, row.y), n: row.n(s), uv: [perimeter[i]!, row.v] };
      },
      { mat, flags },
      shadeRows ? (_i, j) => 0.8 + 0.2 * (j / (list.length - 1)) : undefined,
    );
  wall(rows, wallMat, true);
  if (tileRows.length > 1) wall(tileRows, 'tile', false);

  // Deck from the lip out to the plot rectangle.
  const lipOffset = rc + topOffset;
  for (let i = 0; i < nu; i += 1) {
    const s0 = samples[i]!;
    const s1 = samples[i + 1]!;
    const rectHit = (s: OutlineSample): V3 => {
      const bx = cx + s.base[0];
      const bz = cz + s.base[1];
      let t = Infinity;
      if (s.normal[0] > 1e-6) t = Math.min(t, (plot.x1 - bx) / s.normal[0]);
      if (s.normal[0] < -1e-6) t = Math.min(t, (plot.x0 - bx) / s.normal[0]);
      if (s.normal[1] > 1e-6) t = Math.min(t, (plot.z1 - bz) / s.normal[1]);
      if (s.normal[1] < -1e-6) t = Math.min(t, (plot.z0 - bz) / s.normal[1]);
      return [bx + s.normal[0] * t, 0, bz + s.normal[1] * t];
    };
    const n: [V3, V3, V3] = [UP, UP, UP];
    const a = at(s0, lipOffset, 0);
    const c = at(s1, lipOffset, 0);
    const d = rectHit(s1);
    const e = rectHit(s0);
    b.tri(a, c, d, { mat: deckMat }, { normals: n });
    b.tri(a, d, e, { mat: deckMat }, { normals: n });
  }

  // Coping: a grind loop plus a rounded render lip that overhangs slightly.
  const copingLine: V3[] = [];
  const copingTube: V3[] = [];
  for (let i = 0; i < nu; i += 1) {
    const s = samples[i]!;
    const prev = copingLine[copingLine.length - 1];
    const p = at(s, lipOffset + 0.02, 0.06);
    if (prev && Math.hypot(p[0] - prev[0], p[2] - prev[2]) < 0.05) continue;
    copingLine.push(p);
    copingTube.push(at(s, lipOffset - 0.03, -0.02));
  }
  b.rail('coping', copingLine, true);
  tube(b, copingTube, 0.12, 'coping', { loop: true, sides: 8 });
  b.shape(
    'pool',
    samples.slice(0, nu).map((s) => {
      const p = at(s, lipOffset, 0);
      return [p[0], p[2]] as [number, number];
    }),
  );
}

/** Flat ground tiles over the park, skipping sunken plots. */
export function ground(
  b: CourseBuilder,
  half: number,
  cell: number,
  holes: Rect[],
  materialAt: (x: number, z: number) => Material,
): void {
  for (let x = -half; x < half; x += cell) {
    for (let z = -half; z < half; z += cell) {
      const mx = x + cell / 2;
      const mz = z + cell / 2;
      if (holes.some((h) => mx > h.x0 && mx < h.x1 && mz > h.z0 && mz < h.z1)) continue;
      // Subtle per-slab tint so large areas do not read as one flat colour.
      const hash = Math.sin(mx * 12.9898 + mz * 78.233) * 43758.5453;
      const tint = 0.9 + (hash - Math.floor(hash)) * 0.14;
      b.quad([x, 0, z], [x + cell, 0, z], [x + cell, 0, z + cell], [x, 0, z + cell], UP, {
        mat: materialAt(mx, mz),
        shade: [tint, tint, tint],
      });
    }
  }
}
